import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaClipboardList, FaCheck, FaTrash, FaSpinner } from 'react-icons/fa';
import * as solicitudesService from '../services/solicitudesService';
import '../styles/pages/SolicitudesPage.css';

const ESTADOS = ['PENDIENTE', 'EN_PROCESO', 'RESUELTO'];

const ESTADO_LABELS = {
  PENDIENTE: 'Pendiente',
  EN_PROCESO: 'En proceso',
  RESUELTO: 'Resuelto',
};

/**
 * Dashboard de solicitudes para el contador.
 * Muestra todas las peticiones enviadas por los clientes desde Acciones Rápidas.
 */
const SolicitudesPage = ({ token }) => {
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  // edits: { [id]: { estado?, notas? } } — cambios pendientes de guardar por fila
  const [edits, setEdits] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await solicitudesService.getSolicitudes(null, filtroEstado || undefined);
      setSolicitudes(res.data || []);
    } catch (err) {
      console.error('[SolicitudesPage] Error al cargar:', err);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const setEdit = (id, campo, valor) =>
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));

  const tieneEdits = (s) => {
    const edit = edits[s.id];
    if (!edit) return false;
    return (edit.estado !== undefined && edit.estado !== s.estado)
      || (edit.notas !== undefined && edit.notas !== (s.notas_contador || ''));
  };

  const handleGuardar = async (s) => {
    const edit = edits[s.id] || {};
    const estado = edit.estado ?? s.estado;
    const notas = edit.notas !== undefined ? edit.notas : s.notas_contador;
    try {
      await solicitudesService.cambiarEstado(s.id, estado, notas);
      setEdits(prev => { const next = { ...prev }; delete next[s.id]; return next; });
      cargar();
    } catch (err) {
      console.error('[handleGuardar] Error:', err);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try {
      await solicitudesService.deleteSolicitud(id);
      cargar();
    } catch (err) {
      console.error('[handleEliminar] Error:', err);
    }
  };

  const pendientes = solicitudes.filter(s => s.estado === 'PENDIENTE').length;

  return (
    <div className="sol-page">
      <header className="sol-header">
        <button className="sol-back-btn" onClick={() => navigate('/dashboard')}>
          <FaArrowLeft /> Volver
        </button>
        <div className="sol-header-info">
          <h1 className="sol-title">
            <FaClipboardList /> Solicitudes de Clientes
            {pendientes > 0 && <span className="sol-badge">{pendientes}</span>}
          </h1>
          <p className="sol-subtitle">Peticiones enviadas por los clientes desde Acciones Rápidas</p>
        </div>
      </header>

      <div className="sol-filters">
        <select
          className="sol-select"
          value={filtroEstado}
          onChange={e => setFiltroEstado(e.target.value)}
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => (
            <option key={e} value={e}>{ESTADO_LABELS[e]}</option>
          ))}
        </select>
        <span className="sol-count">{solicitudes.length} solicitud(es)</span>
      </div>

      {loading ? (
        <div className="sol-loading"><FaSpinner className="sol-spinner" /> Cargando...</div>
      ) : solicitudes.length === 0 ? (
        <p className="sol-empty">
          No hay solicitudes{filtroEstado ? ` con estado "${ESTADO_LABELS[filtroEstado]}"` : ''}
        </p>
      ) : (
        <div className="sol-table-wrapper">
          <table className="sol-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Empresa</th>
                <th>Solicitud</th>
                <th>Estado</th>
                <th>Notas del contador</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {solicitudes.map(s => {
                const edit = edits[s.id] || {};
                const estadoActual = edit.estado ?? s.estado;
                const notasActuales = edit.notas !== undefined ? edit.notas : (s.notas_contador || '');

                return (
                  <tr key={s.id} className={`sol-row sol-row--${s.estado.toLowerCase()}`}>
                    <td className="sol-fecha">
                      {new Date(s.fecha_creacion).toLocaleString('es-CL', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                        timeZone: 'America/Santiago',
                      })}
                    </td>
                    <td className="sol-empresa">{s.nombre_empresa || `Empresa #${s.id_company}`}</td>
                    <td className="sol-tipo">{s.tipo}</td>
                    <td>
                      <select
                        className={`sol-estado-select sol-estado--${estadoActual.toLowerCase()}`}
                        value={estadoActual}
                        onChange={e => setEdit(s.id, 'estado', e.target.value)}
                      >
                        {ESTADOS.map(e => (
                          <option key={e} value={e}>{ESTADO_LABELS[e]}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="sol-notas-input"
                        placeholder="Agregar notas..."
                        value={notasActuales}
                        onChange={e => setEdit(s.id, 'notas', e.target.value)}
                      />
                    </td>
                    <td className="sol-acciones">
                      {tieneEdits(s) && (
                        <button className="sol-btn sol-btn--save" onClick={() => handleGuardar(s)}>
                          <FaCheck /> Guardar
                        </button>
                      )}
                      <button className="sol-btn sol-btn--delete" onClick={() => handleEliminar(s.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SolicitudesPage;
